import React from "react";
import "../styles/privacy.css"; // Create this for styling

const PrivacyPolicy = () => {
  return (
    <div className="privacy-container">
      <h1>Privacy Policy</h1>
      <p><strong>TrackSmart Solutions</strong></p>
      <p><strong>Effective Date:</strong> April 19, 2025</p>
      <p>
        At TrackSmart Solutions (“we,” “us,” or “our”), we are committed to protecting the privacy and security of your personal data. This Privacy Policy explains how we collect, use, store, share, and protect personal data in connection with our fleet tracking services, including our subscription plans (Starter, Pro, Enterprise), tracking devices, and “Track & Earn” referral program. We operate in compliance with the Nigeria Data Protection Regulation (NDPR) and other applicable laws.
      </p>
      <p>
        By using our services, registering via our Customer Registration Form, visiting our website (<a href="https://tracksmartng.com">tracksmartng.com</a>), or interacting with us (e.g., via WhatsApp, email, or phone), you consent to the practices described in this policy. If you have questions, contact us at <a href="mailto:info@tracksmartng.com">info@tracksmartng.com</a> or <a href="tel:+2349027161192">+2349027161192</a>.
      </p>

      <h2>1. Who We Are</h2>
      <p>
        TrackSmart Solutions is a Nigerian fleet tracking service provider, offering real-time GPS tracking, SMS tracking, fuel monitoring, and analytics for businesses and individuals. Our registered address is at Sawmill Ado road, Ikere-Ekiti. We are committed to safeguarding your personal data and ensuring transparency in our data practices.
      </p>

      <h2>2. Personal Data We Collect</h2>
      <p>We collect personal data to provide, improve, and manage our services. The types of data we collect depend on how you interact with us (e.g., as a customer, website visitor, or referral participant). Below are the categories of data we collect:</p>
      <h3>2.1 Data You Provide</h3>
      <ul>
        <li><strong>Customer Registration Form:</strong>
          <ul>
            <li>Business or Individual Name (e.g., company name, sole proprietor name, or personal name).</li>
            <li>Business Registration Number (CAC, if applicable).</li>
            <li>Contact Details: Phone number, WhatsApp number, email address.</li>
            <li>Address: Street address, city, state, nearest landmark.</li>
            <li>Vehicle Information: Vehicle type (e.g., motorcycle, car, truck), registration number (if available), model/year, use (e.g., delivery, personal).</li>
            <li>Billing Information: Payment method (e.g., bank transfer, Paystack), billing contact details.</li>
            <li>Referral Details: Referrer’s name or code (if applicable).</li>
          </ul>
        </li>
        <li><strong>Service Interactions:</strong>
          <ul>
            <li>Payment details (e.g., bank account or card information via Paystack/Flutterwave).</li>
            <li>Correspondence (e.g., support requests via WhatsApp, email, or phone).</li>
            <li>Feedback or survey responses.</li>
            <li>Referral Program: Names and contact details of referred customers.</li>
          </ul>
        </li>
      </ul>
      <h3>2.2 Data Collected Automatically</h3>
      <ul>
        <li><strong>Tracking Device Data:</strong>
          <ul>
            <li>Vehicle location, speed, route history, fuel usage, and driver behavior (e.g., harsh braking), collected via GPS trackers installed in your vehicles.</li>
            <li>Device status (e.g., active, disabled, tampered).</li>
          </ul>
        </li>
        <li><strong>Website and App Usage:</strong>
          <ul>
            <li>IP address, browser type, device type, and operating system.</li>
            <li>Pages visited, time spent, and actions taken (e.g., form submissions).</li>
            <li>Cookies or similar technologies for analytics and user experience (see Section 8).</li>
          </ul>
        </li>
        <li><strong>Service Usage:</strong>
          <ul>
            <li>Login activity, feature usage (e.g., analytics reports), and alert preferences.</li>
          </ul>
        </li>
      </ul>
      <h3>2.3 Data from Third Parties</h3>
      <ul>
        <li>Payment Processors: Transaction details from Paystack, Flutterwave, or banks to verify payments.</li>
        <li>Referral Program: Data from referred customers to track bonuses.</li>
        <li>Public Sources: Business registration details (e.g., CAC database) for verification, if applicable.</li>
      </ul>

      <h2>3. How We Use Your Personal Data</h2>
      <p>We use your personal data to provide and improve our services, comply with legal obligations, and communicate with you. Below are the purposes and legal bases for processing under NDPR:</p>
      <table>
        <thead>
          <tr>
            <th>Purpose</th>
            <th>Data Used</th>
            <th>Legal Basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Provide fleet tracking services (e.g., GPS tracking, alerts, analytics)</td>
            <td>Contact details, vehicle data, tracking device data</td>
            <td>Performance of contract (Service Agreement)</td>
          </tr>
          <tr>
            <td>Process payments and manage billing</td>
            <td>Billing information, payment details</td>
            <td>Performance of contract, legitimate interest (financial management)</td>
          </tr>
          <tr>
            <td>Coordinate installation and uninstallation of tracking devices</td>
            <td>Contact details, address, vehicle information</td>
            <td>Performance of contract</td>
          </tr>
          <tr>
            <td>Administer the “Track & Earn” referral program</td>
            <td>Referral details, contact details</td>
            <td>Consent, legitimate interest (business growth)</td>
          </tr>
          <tr>
            <td>Respond to support requests and inquiries</td>
            <td>Contact details, correspondence</td>
            <td>Performance of contract, legitimate interest (customer service)</td>
          </tr>
          <tr>
            <td>Send billing reminders, service updates, and alerts</td>
            <td>Contact details, service usage data</td>
            <td>Performance of contract, legitimate interest (service delivery)</td>
          </tr>
          <tr>
            <td>Improve services and develop new features</td>
            <td>Usage data, feedback, analytics</td>
            <td>Legitimate interest (service enhancement)</td>
          </tr>
          <tr>
            <td>Market our services (e.g., promotions, referral bonuses)</td>
            <td>Contact details, referral data</td>
            <td>Consent (you may opt out)</td>
          </tr>
          <tr>
            <td>Comply with legal obligations (e.g., NDPR, tax laws)</td>
            <td>Business/individual details, payment records</td>
            <td>Legal obligation</td>
          </tr>
          <tr>
            <td>Prevent fraud and manage defaults (e.g., device recovery)</td>
            <td>Contact details, vehicle data, payment history</td>
            <td>Legitimate interest (asset protection, financial security)</td>
          </tr>
        </tbody>
      </table>

      <h2>4. How We Share Your Personal Data</h2>
      <p>We do not sell your personal data. We share data only as necessary to provide services, comply with laws, or protect our rights. Recipients include:</p>
      <ul>
        <li><strong>Service Providers:</strong>
          <ul>
            <li>Payment Processors: Paystack, Flutterwave, or banks to process payments.</li>
            <li>Cloud Hosting: Secure cloud providers (e.g., AWS, Google Cloud) for data storage, configured to NDPR standards.</li>
            <li>Technicians: Local technicians for device installation/uninstallation, receiving only necessary details (e.g., address, vehicle type).</li>
            <li>Customer Support: Third-party platforms (e.g., Zendesk) for managing inquiries.</li>
          </ul>
        </li>
        <li><strong>Referral Program:</strong> Limited data (e.g., referrer’s name) to verify referrals and credit bonuses.</li>
        <li><strong>Legal Authorities:</strong> Government agencies or courts if required by law (e.g., tax audits, NDPR investigations).</li>
        <li><strong>Debt Collection Agencies:</strong> In cases of default, to recover payments or devices, with minimal data shared (e.g., contact details, outstanding fees).</li>
        <li><strong>Business Transfers:</strong> If TrackSmart Solutions is acquired or merged, data may be transferred to the new entity under NDPR-compliant terms.</li>
      </ul>
      <p>All third parties are bound by confidentiality agreements and NDPR-compliant data protection measures.</p>

      <h2>5. Data Storage and Security</h2>
      <h3>5.1 Storage</h3>
      <p>
        Personal data is stored on secure servers with NDPR-compliant encryption and access controls. Data is retained for the duration of your subscription and up to one (1) year after termination, unless required longer for legal purposes (e.g., tax records). Tracking device data (e.g., location history) is retained for six (6) months post-termination for analytics, then anonymized.
      </p>
      <h3>5.2 Security Measures</h3>
      <ul>
        <li>Encryption of data in transit (e.g., SSL) and at rest.</li>
        <li>Access controls limiting data to authorized staff.</li>
        <li>Regular security audits and NDPR compliance checks.</li>
        <li>Firewalls and intrusion detection systems to prevent unauthorized access.</li>
        <li>Secure payment processing via NDPR-compliant providers (Paystack, Flutterwave).</li>
      </ul>

      <h2>6. Your Data Protection Rights</h2>
      <p>Under NDPR, you have the following rights regarding your personal data:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
        <li><strong>Rectification:</strong> Correct inaccurate or incomplete data.</li>
        <li><strong>Erasure:</strong> Request deletion of your data, subject to legal retention requirements (e.g., tax records).</li>
        <li><strong>Restriction:</strong> Limit how we process your data in certain cases (e.g., during a dispute).</li>
        <li><strong>Objection:</strong> Object to processing for marketing or legitimate interests (e.g., analytics).</li>
        <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format.</li>
        <li><strong>Withdraw Consent:</strong> Opt out of marketing communications at any time.</li>
      </ul>
      <p>
        To exercise these rights, contact us at <a href="mailto:info@tracksmartng.com">info@tracksmartng.com</a> or <a href="tel:+2349027161192">+2349027161192</a>. We will respond within thirty (30) days, as required by NDPR. You may also lodge a complaint with the National Information Technology Development Agency (NITDA) if you believe your rights are violated.
      </p>

      <h2>7. Marketing Communications</h2>
      <p>
        7.1 We may send you service-related communications (e.g., billing reminders, installation schedules, service updates) via SMS, WhatsApp, or email, as necessary to fulfill our contract.
      </p>
      <p>
        7.2 With your consent, we may send promotional messages (e.g., referral program bonuses, discounts). You can opt out by:
      </p>
      <ul>
        <li>Clicking “Unsubscribe” in emails.</li>
        <li>Replying “STOP” to SMS/WhatsApp messages.</li>
        <li>Contacting us at <a href="mailto:info@tracksmartng.com">info@tracksmartng.com</a> or <a href="tel:+2349027161192">+2349027161192</a>.</li>
      </ul>
      <p>
        7.3 Opting out of marketing does not affect service-related communications.
      </p>

      <h2>8. Cookies and Tracking Technologies</h2>
      <p>
        8.1 Our website (<a href="https://tracksmartng.com">tracksmartng.com</a>) and tracking platform use cookies and similar technologies to:
      </p>
      <ul>
        <li>Enhance user experience (e.g., remembering login details).</li>
        <li>Analyze usage (e.g., pages visited, feature clicks).</li>
        <li>Deliver personalized content (e.g., tailored promotions).</li>
      </ul>
      <p>
        8.2 Types of cookies:
      </p>
      <ul>
        <li><strong>Essential:</strong> Required for website functionality (e.g., form submissions).</li>
        <li><strong>Analytics:</strong> Track usage for service improvement (e.g., Google Analytics, anonymized).</li>
        <li><strong>Marketing:</strong> Support targeted promotions (optional).</li>
      </ul>
      <p>
        8.3 You can manage cookie preferences via your browser settings or our website’s cookie banner. Disabling cookies may limit website functionality.
      </p>

      <h2>9. International Data Transfers</h2>
      <p>
        9.1 Most of your data is stored and processed within Nigeria to comply with NDPR. If we use international cloud providers (e.g., AWS servers outside Nigeria), we ensure NDPR-compliant safeguards, such as:
      </p>
      <ul>
        <li>Data processing agreements with standard contractual clauses.</li>
        <li>Encryption and anonymization to protect data.</li>
      </ul>
      <p>
        9.2 We will notify you and obtain consent if international transfers involve non-NDPR-compliant jurisdictions.
      </p>

      <h2>10. Data Breaches</h2>
      <p>
        10.1 In the unlikely event of a data breach, we will:
      </p>
      <ul>
        <li>Notify NITDA within seventy-two (72) hours, as required by NDPR.</li>
        <li>Inform affected customers via email, SMS, or WhatsApp within a reasonable timeframe.</li>
        <li>Take immediate steps to mitigate harm (e.g., resetting access, enhancing security).</li>
      </ul>
      <p>
        10.2 We maintain a data breach response plan to ensure swift action.
      </p>

      <h2>11. Third-Party Links</h2>
      <p>
        Our website or communications may include links to third-party sites (e.g., Paystack, social media). These sites have their own privacy policies, and we are not responsible for their practices. Review their policies before sharing data.
      </p>

      <h2>12. Children’s Privacy</h2>
      <p>
        Our services are not intended for individuals under 18. We do not knowingly collect personal data from children. If you believe we have such data, contact us at <a href="mailto:info@tracksmartng.com">info@tracksmartng.com</a> to request deletion.
      </p>

      <h2>13. Updates to This Privacy Policy</h2>
      <p>
        We may update this policy to reflect changes in our services, legal requirements, or customer feedback. Updates will be posted on our website (<a href="https://tracksmartng.com">tracksmartng.com</a>) with the new effective date. Significant changes will be communicated via email or WhatsApp at least thirty (30) days in advance. Continued use of our services constitutes acceptance of the updated policy.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        For questions, requests, or complaints about this Privacy Policy or your data, contact our Data Protection Officer:
      </p>
      <p>
        <strong>TrackSmart Solutions</strong><br />
        Email: <a href="mailto:info@tracksmartng.com">info@tracksmartng.com</a><br />
        Phone/WhatsApp: <a href="tel:+2349027161192">+2349027161192</a>
      </p>
    </div>
  );
};

export default PrivacyPolicy;